import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Popconfirm, Select, Spin, Table, message } from 'antd';
import { getUsers } from '../../api/UsersContainer';
import {
    ACCOUNTING_EMAIL_ROLE,
    COMPLIANCE_CHECK_ROLE,
    FINANCIAL_CONTROLLER_ROLE,
    FINANCIAL_CONTROLLER_ROLES,
    FIRST_APPROVER_ROLE,
    TREASURY_EMAIL_ROLE,
    deleteFinancialController,
    getFinancialControllers,
    insertFinancialController,
} from '../../api/FinancialControllersContainer';

const { Option } = Select;
const { Search } = Input;

const ROLE_FILTER_ALL = 'All';
const ROLE_SORT_ORDER = {
    [FINANCIAL_CONTROLLER_ROLE]: 1,
    [COMPLIANCE_CHECK_ROLE]: 2,
    [TREASURY_EMAIL_ROLE]: 3,
    [ACCOUNTING_EMAIL_ROLE]: 4,
    [FIRST_APPROVER_ROLE]: 5,
};

const SINGLE_ASSIGNEE_ROLES = new Set([
    FINANCIAL_CONTROLLER_ROLE,
    COMPLIANCE_CHECK_ROLE,
]);

const getRoleSortOrder = (role) => ROLE_SORT_ORDER[role] || 99;

const FinancialControllers = () => {
    const [assignments, setAssignments] = useState([]);
    const [roleFilter, setRoleFilter] = useState(ROLE_FILTER_ALL);
    const [loading, setLoading] = useState(false);
    const [role, setRole] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [users, setUsers] = useState([]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersData, assignmentsData] = await Promise.all([
                getUsers(),
                getFinancialControllers(),
            ]);
            setUsers(usersData);
            setAssignments(assignmentsData);
        } catch (error) {
            message.error(error.message || 'Financial controllers could not be loaded');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const availableUsers = useMemo(() => {
        const normalizeEmail = (email) => (email || '').trim().toLowerCase();

        return users.filter((user) => {
            if (!role) {
                return true;
            }

            const userAssignments = assignments.filter((assignment) => (
                assignment.userId === user.id ||
                normalizeEmail(assignment.email) === normalizeEmail(user.email)
            ));

            if (userAssignments.some((assignment) => assignment.role === role)) {
                return false;
            }

            if (SINGLE_ASSIGNEE_ROLES.has(role)) {
                return userAssignments.length === 0;
            }

            return !userAssignments.some((assignment) => SINGLE_ASSIGNEE_ROLES.has(assignment.role));
        });
    }, [assignments, role, users]);

    const filteredAssignments = useMemo(() => {
        const normalizedSearchText = searchText.trim().toLowerCase();

        return assignments
            .filter((assignment) => (
                roleFilter === ROLE_FILTER_ALL ||
                assignment.role === roleFilter
            ))
            .filter((assignment) => {
                if (!normalizedSearchText) {
                    return true;
                }

                return [
                    assignment.name,
                    assignment.email,
                    assignment.role,
                ].some((value) => value?.toLowerCase().includes(normalizedSearchText));
            })
            .sort((a, b) => (
                getRoleSortOrder(a.role) - getRoleSortOrder(b.role) ||
                a.name.localeCompare(b.name)
            ));
    }, [assignments, roleFilter, searchText]);

    const isRoleDisabled = (roleName) => (
        SINGLE_ASSIGNEE_ROLES.has(roleName) &&
        assignments.some((assignment) => assignment.role === roleName)
    );

    const handleAdd = async () => {
        if (!selectedUserId || !role) {
            message.warning('Please select a user and role');
            return;
        }

        const selectedUser = users.find((user) => user.id === selectedUserId);

        setLoading(true);
        try {
            await insertFinancialController(selectedUser, role);
            message.success('Financial controller role added');
            setSelectedUserId(null);
            setRole(null);
            await loadData();
        } catch (error) {
            message.error(error.message || 'Role could not be added');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            await deleteFinancialController(id);
            message.success('Financial controller role deleted');
            await loadData();
        } catch (error) {
            message.error(error.message || 'Role could not be deleted');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a.email.localeCompare(b.email),
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            sorter: (a, b) => (
                getRoleSortOrder(a.role) - getRoleSortOrder(b.role) ||
                a.role.localeCompare(b.role)
            ),
        },
        {
            title: 'Delete',
            key: 'delete',
            render: (_, record) => (
                <Popconfirm
                    title="Delete role"
                    description={`Remove ${record.role} from ${record.name}?`}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(record.id)}
                >
                    <Button danger>Delete</Button>
                </Popconfirm>
            ),
        },
    ];

    if (loading && assignments.length === 0 && users.length === 0) {
        return (
            <Spin
                size="large"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flex: 1, marginTop: -100 }}
            />
        );
    }

    return (
        <div className="management-page">
            <div className="page-heading-row">
                <div className="page-heading-copy">
                    <span className="page-kicker">Approvals</span>
                    <h1>Financial Controllers</h1>
                    <p>Assign finance, compliance, and first approver roles from existing users.</p>
                </div>
            </div>
            <div className="management-list-card full-width">
                <div className="role-assignment-card">
                    <div className="card-heading">
                        <h2>Add Role Assignment</h2>
                        <p>Financial Controller and Compliance Check are single-seat roles. Other roles can be assigned multiple times.</p>
                    </div>
                    <div className="role-assignment-toolbar">
                        <Select
                            showSearch
                            value={selectedUserId}
                            placeholder="Select user"
                            optionFilterProp="children"
                            onChange={setSelectedUserId}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {availableUsers.map((user) => (
                                <Option key={user.id} value={user.id}>
                                    {`${user.name} (${user.email})`}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            value={role}
                            placeholder="Select role"
                            onChange={(selectedRole) => {
                                setRole(selectedRole);
                                setSelectedUserId(null);
                            }}
                        >
                            {FINANCIAL_CONTROLLER_ROLES.map((roleOption) => (
                                <Option key={roleOption} value={roleOption} disabled={isRoleDisabled(roleOption)}>
                                    {roleOption}
                                </Option>
                            ))}
                        </Select>
                        <Button type="primary" onClick={handleAdd} loading={loading}>
                            Add
                        </Button>
                    </div>
                </div>
                <div className="list-panel-heading">
                    <div>
                        <h2>Assignments</h2>
                        <p>{filteredAssignments.length} assignments shown</p>
                    </div>
                    <div className="list-panel-actions">
                        <Search
                            allowClear
                            placeholder="Search name, email, or role"
                            className="list-search"
                            onChange={(event) => setSearchText(event.target.value)}
                        />
                        <Select
                            value={roleFilter}
                            onChange={setRoleFilter}
                        >
                            <Option value={ROLE_FILTER_ALL}>All Roles</Option>
                            <Option value={FINANCIAL_CONTROLLER_ROLE}>Financial Controller</Option>
                            <Option value={COMPLIANCE_CHECK_ROLE}>Compliance Check</Option>
                            <Option value={TREASURY_EMAIL_ROLE}>Treasury Email</Option>
                            <Option value={ACCOUNTING_EMAIL_ROLE}>Accounting Email</Option>
                            <Option value={FIRST_APPROVER_ROLE}>Others</Option>
                        </Select>
                    </div>
                </div>
                <div className="data-table-shell">
                    <Table
                        className="management-table"
                        columns={columns}
                        dataSource={filteredAssignments}
                        loading={loading}
                        rowKey="id"
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default FinancialControllers;
